import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Divider,
  FormInputText,
  Modal,
  Select,
  Skeleton,
  Svg,
  Text,
} from '@hopskipdrive/react-components';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import OpenInNew from '@mui/icons-material/OpenInNew';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { Card } from '../../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { colorTokens } from '../../../../../shared/components/variables';
import { ErrorBoundary } from '../../../../../shared/utils/errorReporting/ErrorBoundary';
import { useIsMobileOrTablet } from '../../../../../shared/utils/helpers/util';
import { CarePartnerCard } from '../../../../components/CarePartnerCard';
import { DriverHeader } from '../../../../components/DriverHeader';
import { DriverInfoContainer } from '../../../../components/DriverInfoContainer';
import { DriverPersonalInfo } from '../../../../components/DriverPersonalInfo';
import { UpcomingRides } from '../../../../components/UpcomingRides';
import { useDriverDetailsQuery } from '../../../../hooks/useDriverDetailsQuery';
import { useRideUnclaimSubmit } from '../../../../hooks/useRideUnclaimSubmit';
import { Header, InfoSection, StyledChip } from '../styles';

import { removeDriverValidationSchema } from './removeDriverValidationSchema';

interface DriverDetailsProps {
  rideState: string;
  accountType: string;
  isCarePartnerRide?: boolean;
}

export const DriverDetails: FC<DriverDetailsProps> = ({ rideState, accountType, isCarePartnerRide = false }) => {
  const { rideSlug } = useParams();
  const { data: driverDetailsResult, isFetching } = useDriverDetailsQuery({ rideSlug: rideSlug || '' });
  const [removeDriverModalOpen, setRemoveDriverModalOpen] = useState<boolean>(false);
  const hideRemoveDriverButtonStates = ['complete', 'canceled'];
  const showRemoveDriverButton = !hideRemoveDriverButtonStates.includes(rideState) && !isCarePartnerRide;
  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <Card>
      <Header>
        <DriverHeader
          driverDetails={driverDetailsResult?.data}
          showRemoveDriverButton={showRemoveDriverButton}
          setRemoveDriverModalOpen={setRemoveDriverModalOpen}
        />
      </Header>
      <Box display="flex" gap="20px" sx={{ width: '100%' }} flexDirection={isMobileOrTablet ? 'column' : 'row'}>
        <Box sx={{ width: isMobileOrTablet ? '100%' : '50%' }} alignItems="flex-end">
          <DriverPersonalInfo driverDetails={driverDetailsResult?.data} isFetching={isFetching} />
          <Box mb={3}>
            <DriverInfoContainer isFetching={isFetching}>
              <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Phone Number Copied"
                text={driverDetailsResult?.data.mobilePhoneNumber || ''}
                variant="h6"
                color={colorTokens.text.secondary}
              />
            </DriverInfoContainer>
            <DriverInfoContainer isFetching={isFetching}>
              <EmailIcon style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Email Copied"
                text={driverDetailsResult?.data.email || ''}
                variant="h6"
                color={colorTokens.text.secondary}
                truncate={true}
              />
            </DriverInfoContainer>
            <DriverInfoContainer isFetching={isFetching}>
              <Svg icon="location-dot" width="24" height="24" style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Address Copied"
                text={driverDetailsResult?.data.address || ''}
                variant="h6"
                color={colorTokens.text.secondary}
              />
            </DriverInfoContainer>
          </Box>
          <Box mb={3}>
            {driverDetailsResult?.data.primaryCaredriver && (
              <StyledChip label="Primary CareDriver" variant="filled" size="small" type="square" />
            )}
          </Box>
          <Box mb={3}>
            <DriverInfoContainer isFetching={isFetching}>
              <Svg icon="directions-car-filled" width="16" height="16" />
              <Text variant="caption" textColor="secondaryText" fontWeight={500}>
                {driverDetailsResult?.data.rideCount} Trips Activated {driverDetailsResult?.data.activatedOn}
              </Text>
            </DriverInfoContainer>
          </Box>
        </Box>
        <Box sx={{ width: isMobileOrTablet ? '100%' : '50%' }} alignItems="flex-end">
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Gender
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{driverDetailsResult?.data?.gender}</Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Seats
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{driverDetailsResult?.data?.vehicleInformation.seats}</Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Booster Seats
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6" textColor="primaryText">
                {driverDetailsResult?.data?.vehicleInformation.boosters || '--'}
              </Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Plate
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{driverDetailsResult?.data?.vehicleInformation.licensePlate}</Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Make
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{driverDetailsResult?.data?.vehicleInformation.yearMakeAndModel}</Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Color
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{driverDetailsResult?.data?.vehicleInformation.color}</Text>
            )}
          </InfoSection>
          <Box mt={2} padding="12px 0px">
            <Button
              color="tonal"
              variant="text"
              startIcon={<OpenInNew />}
              style={{ border: `1px solid ${colorTokens.primary.outlineBorder}` }}
              onClick={() => window.open(driverDetailsResult?.data.latestInvoice, '_blank')}
            >
              Latest Invoice
            </Button>
          </Box>
        </Box>
      </Box>
      {isCarePartnerRide && (
        <CarePartnerCard
          isFetching={isFetching}
          name={driverDetailsResult?.data?.carePartnerDetails?.name}
          certifiedDriversCount={driverDetailsResult?.data?.carePartnerDetails?.certifiedDriverCount}
          phoneNumber={driverDetailsResult?.data?.carePartnerDetails?.businessPhoneNumber}
          url={driverDetailsResult?.data?.carePartnerDetails?.adminCarePartnerDetailsLink}
        />
      )}
      {accountType === 'B2B' && (
        <InfoSection>
          <Text variant="h6">Contact Permissions</Text>
          <ContactPermissionContainer isFetching={isFetching}>
            {driverDetailsResult?.data.contactPermissions?.map((permission) => (
              <ContactPermissionItem key={permission.label}>
                {permission.value ? (
                  <CheckIcon data-testid="check-icon" sx={{ color: colorTokens.success.main }} />
                ) : (
                  <NotInterestedRoundedIcon data-testid="not-interested-icon" sx={{ color: colorTokens.error.main }} />
                )}
                <Text>{permission.label}</Text>
              </ContactPermissionItem>
            ))}
          </ContactPermissionContainer>
        </InfoSection>
      )}
      <Box gap="4px" width="100%">
        <ErrorBoundary>
          <UpcomingRides userSlug={driverDetailsResult?.data.slug} />
        </ErrorBoundary>
      </Box>
      <RemoveDriverModal
        rideSlug={rideSlug}
        isOpen={removeDriverModalOpen}
        reasons={driverDetailsResult?.data.driverRemovalReasons || []}
        setRemoveDriverModalOpen={setRemoveDriverModalOpen}
      />
    </Card>
  );
};

const ContactPermissionContainer = ({ isFetching, children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="flex-start" gap="9px">
      {isFetching ? (
        <>
          <Skeleton data-testid="contact-permission-skeleton-loader" variant="text" width={10} height={42} />
          <Skeleton variant="text" width={263} height={42} />
          <Skeleton variant="text" width={10} height={42} />
          <Skeleton variant="text" width={263} height={42} />
        </>
      ) : (
        children
      )}
    </Box>
  );
};

const ContactPermissionItem = ({ children }) => {
  return (
    <Box
      data-testid="contact-permission"
      display="flex"
      flexDirection="row"
      alignItems="center"
      gap="9px"
      padding="6px 9px"
    >
      {children}
    </Box>
  );
};

const RemoveDriverModal = ({ rideSlug, isOpen, reasons, setRemoveDriverModalOpen }) => {
  const { mutate, isPending } = useRideUnclaimSubmit({
    rideSlug: rideSlug,
    onSuccess: () => {
      setRemoveDriverModalOpen(false);
    },
    onError: () => {
      setRemoveDriverModalOpen(false);
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ driverRemovalReasonKey: string }>({
    defaultValues: {
      driverRemovalReasonKey: '',
    },
    resolver: yupResolver(removeDriverValidationSchema),
  });

  const handleRemoveDriver = ({ driverRemovalReasonKey }) => {
    const unclaimRidePayload = {
      removeDriver: {
        driverRemovalReason: driverRemovalReasonKey,
      },
    };

    mutate(unclaimRidePayload);
  };

  return (
    <Modal open={isOpen}>
      <Modal.Title>Are you sure you want to remove driver?</Modal.Title>
      <Modal.Content>
        <Box>
          <Box mb={2}>
            <Text variant="body1" textColor="secondaryText">
              This will unclaim the ride.
            </Text>
          </Box>
          <Box display="flex" alignItems="center" gap="60px" mb={4}>
            <Text variant="body2" textColor="secondaryText">
              Reason<span style={{ color: colorTokens.error.main }}>*</span>
            </Text>
            <Box width="100%">
              <form id="remove-driver-form" onSubmit={handleSubmit(handleRemoveDriver)}>
                <Controller
                  control={control}
                  name="driverRemovalReasonKey"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      options={reasons.map((reasonObject) => ({
                        label: reasonObject[1],
                        value: reasonObject[0],
                      }))}
                      value={value}
                      setValue={onChange}
                      renderInput={(params) => (
                        <FormInputText
                          {...params}
                          variant="outlined"
                          data-testid="locationType"
                          fullWidth
                          placeholder="Select"
                          error={!!errors.driverRemovalReasonKey}
                          helperText={errors.driverRemovalReasonKey?.message}
                        />
                      )}
                    />
                  )}
                />
              </form>
            </Box>
          </Box>
        </Box>
      </Modal.Content>
      <Divider />
      <Modal.Actions>
        <Button color="tonal" disabled={isPending} onClick={() => setRemoveDriverModalOpen(false)}>
          Never mind
        </Button>
        <Button color="primary" form="remove-driver-form" type="submit" loading={isPending}>
          {isPending ? 'Removing...' : 'Remove Driver'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const DriverDetailsSkeleton = ({ accountType }) => {
  return (
    <Card>
      <Header>
        <Box display="flex" alignItems="center" gap="12px">
          <Svg
            icon="directions-car-filled"
            style={{
              display: 'block',
              height: '24px',
              width: '24px',
              alignSelf: 'center',
              color: colorTokens.primary.main,
            }}
          />
          <Text variant="h6" style={{ color: colorTokens.primary.main }}>
            Driver
          </Text>
        </Box>
        <Button
          color="warning"
          startIcon={<Svg icon="cross" />}
          disabled
          style={{
            border: `1px solid ${colorTokens.error.outlineBorder}`,
            color: `${colorTokens.error.dark}`,
          }}
        >
          Remove Driver
        </Button>
      </Header>
      <Box display="flex" gap="20px" sx={{ width: '100%' }}>
        <Box sx={{ width: '50%' }} alignItems="flex-end">
          <Box display="flex" justifyContent="flex-start" alignItems="flex-end" gap={'55px'}>
            <Skeleton data-testid="main-heading-skeleton" variant="circular" width={92} height={92} />
          </Box>
          <Box mb={3}>
            <Skeleton variant="text" width={135} height={32} />
            <Skeleton variant="text" width={130} height={24} />
          </Box>
          <Box mb={3}>
            <DriverInfoContainer isFetching={true}>{''}</DriverInfoContainer>
            <DriverInfoContainer isFetching={true}>{''}</DriverInfoContainer>
            <DriverInfoContainer isFetching={true}>{''}</DriverInfoContainer>
          </Box>

          <Box mb={3}>
            <DriverInfoContainer isFetching={true}>{''}</DriverInfoContainer>
          </Box>
        </Box>
        <Box sx={{ width: '50%' }} alignItems="flex-end">
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Seats
            </Text>

            <Skeleton variant="text" width={263} height={42} />
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Booster Seats
            </Text>

            <Skeleton variant="text" width={263} height={42} />
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Plate
            </Text>

            <Skeleton variant="text" width={263} height={42} />
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Make
            </Text>

            <Skeleton variant="text" width={263} height={42} />
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText">
              Color
            </Text>

            <Skeleton variant="text" width={263} height={42} />
          </InfoSection>
          <Box mt={2} padding="12px 0px">
            <Button
              color="tonal"
              variant="text"
              disabled
              startIcon={<OpenInNew />}
              style={{ border: `1px solid ${colorTokens.primary.outlineBorder}` }}
            >
              Latest Invoice
            </Button>
          </Box>
        </Box>
      </Box>
      {accountType === 'B2B' && (
        <InfoSection>
          <Text variant="h6">Contact Permissions</Text>
          <ContactPermissionContainer isFetching={true}>{''}</ContactPermissionContainer>
        </InfoSection>
      )}
    </Card>
  );
};
